import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useAnalytics } from '@/hooks/analytics';
import { useScreenViewLogging } from '@/hooks/emotion-detector';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';

import { emotionDetectorScreens } from '../EmotionDetectorScreens/data';
import { ActiveScreen, EmotionDetectorState } from './types';

export const EmotionDetectorContext = createContext<EmotionDetectorState>({
	activeScreen: 'signup',
	setActiveScreen: () => {},
	activeComponentName: 'SignupScreen',
});

export const useEmotionDetectorContext = (): EmotionDetectorState => useContext(EmotionDetectorContext);

const EmotionDetectorContextProvider = ({ children }: { children: ReactNode }) => {
	const user = useUserState();
	const { logEvent } = useAnalytics();

	// Initialize screen based on user auth state
	const [activeScreen, setActiveScreen] = useState<ActiveScreen>(() => {
		return user ? 'home' : 'signup';
	});

	const logScreenView = useCallback(
		(screenName: string) => {
			logEvent({
				eventName: 'Screen : Viewed',
				eventProps: {
					screen: screenName,
				},
			});
		},
		[logEvent],
	);

	// Log screen view when activeScreen changes
	const analyticsName = emotionDetectorScreens[activeScreen]?.analyticsName;
	useScreenViewLogging(analyticsName, logScreenView);

	// Scroll to top on screen change
	useEffect(() => {
		window.scrollTo(0, 0);
	}, [activeScreen]);

	// Redirect to signup if user logs out
	useComponentDidMount(() => {
		if (!user && activeScreen !== 'signup') {
			setActiveScreen('signup');
		}
	});

	const value = useMemo(
		() => ({
			activeScreen,
			setActiveScreen,
			activeComponentName: emotionDetectorScreens[activeScreen]?.componentName,
		}),
		[activeScreen],
	);

	return <EmotionDetectorContext.Provider value={value}>{children}</EmotionDetectorContext.Provider>;
};

export default EmotionDetectorContextProvider;
